import { useQueries, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Bar, BarChart, CartesianGrid, LabelList, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, FilterBar } from "@/components/data";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  getTplHistory,
  getTplProviders,
  PfcError,
  type TplHistory,
  type TplProvider,
} from "@/modules/fulfilment_costs/api";
import { COMPANY_LABEL } from "@/modules/fulfilment_costs/TplHistoryTab";

import type { PageProps } from "../index";
import { CHART, CRITERIA_COLOURS, TOOLTIP_STYLE } from "../charts";
import { Footnote, money, Note, PageHead } from "../kit";
import {
  actualMonthly,
  actualPerParcel,
  agedStockSaving,
  belowIndexFloor,
  BLOCKS,
  chosenIds,
  companyOptions,
  CURRENT_3PLS,
  decision,
  defaultDrivers,
  defaultFrom,
  defaultTo,
  fetchFrom,
  INDEX_METRICS,
  INDEX_FLOOR,
  indexGaps,
  indexRows,
  isBilled,
  lastBilled,
  lastCompleteMonth,
  legendLine,
  monthsInWords,
  minimumDefault,
  minimumToGbpRatio,
  moveTfiSaving,
  parseList,
  PARTS,
  parcelsPerMonth,
  perParcelByPart,
  rankStandardised,
  regionOptions,
  rates as activityRates,
  runRate,
  selectableProviders,
  shortMonth,
  standardised,
  trendRows,
  validDrivers,
  validFrom,
  validTo,
  withMinimums,
  yearRows,
  ym,
  type Drivers,
  type IndexMetric,
  type PerParcel,
  type PerParcelKey,
  type ProviderId,
  type RunRate,
  type Standardised,
} from "./tplCostModel";
import { MultiPick } from "./MultiPick";

const QUERY_ROOT = ["sourcing", "tpl_cost_model"] as const;
const LINE_COLOURS = [CHART.purple, CHART.purple2, CHART.ink, CHART.warn] as const;
const ROW_ERROR = "We can't reach the fulfilment cost data right now. Try again shortly.";
/** A16: every table fits its box at a 1080 px viewport: fixed layout, wrapping cells, never a sideways scroll. */
const FIT_TABLE = "table-fixed w-full [&_td]:whitespace-normal [&_td]:break-words [&_th]:whitespace-normal [&_th]:align-bottom";
/** Wide tables also stack below md: one block per record, each cell "label: value" from its data-label. */
const WIDE_TABLE = `${FIT_TABLE} text-xs [&_td]:px-1 [&_th]:px-1 max-md:[&_thead]:sr-only max-md:[&_tr]:block max-md:[&_tr]:py-2 max-md:[&_td]:flex max-md:[&_td]:justify-between max-md:[&_td]:gap-3 max-md:[&_td]:py-0.5 max-md:[&_td]:before:content-[attr(data-label)] max-md:[&_td]:before:text-left max-md:[&_td]:before:font-medium`;

const keepLegendBarOrder = () => 0;

function lineColour(id: string, chosen: string[]): string {
  const currentIndex = (CURRENT_3PLS as readonly string[]).indexOf(id);
  if (currentIndex >= 0) return LINE_COLOURS[currentIndex];
  const otherIndex = chosen.filter((chosenId) => !(CURRENT_3PLS as readonly string[]).includes(chosenId)).indexOf(id);
  return CRITERIA_COLOURS[(4 + otherIndex) % CRITERIA_COLOURS.length];
}

const finiteMoney = (value: number | null | undefined, dp = 2): string =>
  value != null && Number.isFinite(value) ? money(value, dp) : "—";
export function moneyIn(value: number | null | undefined, currency: string, dp = 2): string {
  if (currency === "GBP") return finiteMoney(value, dp);
  if (value == null || !Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString("en-GB", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
  }
}
const percent = (value: number | null, suspect = false): string =>
  value == null || !Number.isFinite(value) ? "—" : `${value >= 0 ? "+" : ""}${Math.round(value * 100)}%${suspect ? "*" : ""}`;
const inputNumber = (raw: unknown): number | null => {
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};
const LONG_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const longMonth = (month: string): string => `${LONG_MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
const errorStatus = (error: unknown): number | null => error instanceof PfcError ? error.status : null;

function Failure({ status }: { status: number | null }) {
  const copy = status === 404
    ? "3PL bills are not available on the Fulfilment Costs engine yet."
    : status === 403
      ? "This page reads Precision Fulfilment's 3PL bills. Ask the Margin Engine area owner for access."
      : "We can't reach the fulfilment cost data right now. Try again shortly.";
  return <Alert><AlertDescription>{copy}</AlertDescription></Alert>;
}

function Pending() {
  return (
    <div className="grid gap-8">
      <ChartFrame title="Actual yearly cost per 3PL, by part" titleVisible pending><BarChart /></ChartFrame>
      <ChartFrame title="Actual monthly cost per 3PL" titleVisible pending><LineChart /></ChartFrame>
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return <h2 className="text-base font-semibold text-ks-ink">{children}</h2>;
}

function scopeCopy(ids: string[], providers: Map<string, TplProvider>, from: string, to: string, measure: string): string {
  return `${ids.map((id) => providers.get(id)?.label ?? id).join(", ")}; ${longMonth(from)} to ${longMonth(to)}; ${measure}.`;
}

function ScopeLine({ copy }: { copy: string }) {
  return <p data-scope className="text-sm text-muted-foreground">{copy}</p>;
}

function providerMap(providers: TplProvider[]): Map<string, TplProvider> {
  return new Map(providers.map((provider) => [provider.provider_id, provider]));
}

function yearlyValue(rate: RunRate, parts: readonly string[]): number | null {
  if (!rate.yearlyParts) return null;
  return parts.reduce((sum, part) => sum + rate.yearlyParts![part as keyof typeof rate.yearlyParts], 0);
}

function EmptyProviderCells({ copy, columns, error = false }: { copy: string; columns: number; error?: boolean }) {
  return <TableCell colSpan={columns} className="text-muted-foreground">{error ? <span role="alert">{copy}</span> : copy}</TableCell>;
}

function emptyRowCopy(id: string, provider: TplProvider | undefined, history: TplHistory | undefined, rate: RunRate | undefined, loading: Set<string>, errors: Set<string>, fetchTo: string): string | null {
  if (!provider) return "Not in the engine yet";
  if (loading.has(id)) return "Loading…";
  if (errors.has(id)) return ROW_ERROR;
  if (!history || !rate) return "No bills in this range";
  if (rate.monthsBilled > 0) return null;
  const last = lastBilled(history, fetchTo);
  return last ? `Last billed ${shortMonth(last)}: move From back to include its months` : "No bills in this range";
}

type RankedStandardised = Standardised & {
  vsCheapest: number | null;
  topUpPerYear?: number;
  binding?: boolean;
};

interface ProviderMinimum {
  id: string;
  currency: string;
  pickPackRaw: string;
  invoiceRaw: string;
  pickPackGbp: number | null;
  invoiceGbp: number | null;
  conversionMissing: boolean;
  pickPackRateMissing: boolean;
  invalidLabels: string[];
}

function itemisedPickPackRate(history: TplHistory, from: string, to: string): number | null {
  const hasPickPackLine = history.months.some((month) => {
    const monthKey = ym(month.month);
    return monthKey >= from
      && monthKey <= to
      && isBilled(history.provider.provider_id, month)
      && month.parcels.total - month.parcels.fba > 0
      && month.costs.some((cost) => cost.cost_type === "pick_pack");
  });
  const pickPack = perParcelByPart(history, from, to).pick_pack;
  return hasPickPackLine && typeof pickPack === "number" ? pickPack : null;
}

function AllProvidersToday({
  ids,
  providers,
  rates,
  from,
  to,
}: {
  ids: string[];
  providers: Map<string, TplProvider>;
  rates: Map<string, RunRate>;
  from: string;
  to: string;
}) {
  const rows = ids.map((id) => {
    const provider = providers.get(id);
    const rate = rates.get(id);
    const monthly = rate ? actualMonthly(rate) : null;
    const lowVolume = monthly != null && monthly.parcels < INDEX_FLOOR;
    return { id, provider, rate, monthly, lowVolume };
  });
  return <section className="grid gap-3">
    <SectionTitle>All 3PLs today</SectionTitle>
    <ScopeLine copy={scopeCopy(ids, providers, from, to, "monthly average over billed months")} />
    <Table scrollable={false} className={WIDE_TABLE}>
      <TableHeader><TableRow>
        <TableHead>3PL</TableHead>
        <TableHead>Region</TableHead>
        <TableHead className="text-right">Months billed</TableHead>
        <TableHead className="text-right">Billed a month</TableHead>
        <TableHead className="text-right">Parcels a month</TableHead>
        <TableHead className="text-right">Cost per parcel</TableHead>
        <TableHead className="text-right">Storage a month</TableHead>
        <TableHead className="text-right">Subscription a month</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map(({ id, provider, rate, monthly, lowVolume }) => <TableRow key={`today-${id}`}>
        <TableCell className="font-medium">{provider?.label ?? id}{lowVolume ? <span className="ml-2 text-xs font-normal text-muted-foreground">low volume</span> : null}</TableCell>
        <TableCell data-label="Region">{provider?.region ?? "—"}</TableCell>
        <TableCell data-label="Months billed" className="text-right">{rate?.monthsBilled ?? 0}</TableCell>
        <TableCell data-label="Billed a month" className="text-right">{moneyIn(monthly?.billed, "GBP", 0)}</TableCell>
        <TableCell data-label="Parcels a month" className="text-right">{monthly ? monthly.parcels.toLocaleString("en-GB", { maximumFractionDigits: 0 }) : "—"}</TableCell>
        <TableCell data-label="Cost per parcel" className="text-right">{monthly?.perParcel == null ? "No parcels" : moneyIn(monthly.perParcel, "GBP", 2)}</TableCell>
        <TableCell data-label="Storage a month" className="text-right">{moneyIn(monthly?.storage, "GBP", 0)}</TableCell>
        <TableCell data-label="Subscription a month" className="text-right">{moneyIn(monthly?.subscription, "GBP", 0)}</TableCell>
      </TableRow>)}</TableBody>
    </Table>
  </section>;
}

function StandardisedCostTable({
  ids, providers, histories, rates, loading, errors, fetchTo, from, to, drivers, driversTyped, d2cRaw, fbaRaw, rows, minimums, invalidDrivers, setSearch, invoiceMode,
}: {
  ids: string[];
  providers: Map<string, TplProvider>;
  histories: Map<string, TplHistory>;
  rates: Map<string, RunRate>;
  loading: Set<string>;
  errors: Set<string>;
  fetchTo: string;
  from: string;
  to: string;
  drivers: Drivers | null;
  driversTyped: boolean;
  d2cRaw: string;
  fbaRaw: string;
  rows: RankedStandardised[];
  minimums: ProviderMinimum[];
  invalidDrivers: boolean;
  setSearch: (key: string, value: string) => void;
  invoiceMode: boolean;
}) {
  const completeRows = rows.filter((row) => row.complete && row.total != null);
  const byId = new Map(rows.map((row) => [row.providerId, row]));
  const incompleteRows = rows.filter((row) => !row.complete && (rates.get(row.providerId)?.monthsBilled ?? 0) > 0);
  const orderedIds = drivers
    ? [...rows.map((row) => row.providerId), ...ids.filter((id) => !rows.some((row) => row.providerId === id))]
    : ids;
  const completeChartRows = drivers ? completeRows.flatMap((row) => {
    const provider = providers.get(row.providerId);
    return provider ? [{
      label: provider.label,
      Fixed: row.blocks.Fixed,
      "Customer parcels": row.blocks["Customer parcels"],
      "Amazon inbound": row.blocks["Amazon inbound"],
      "Other variable": row.blocks["Other variable"],
      ...(row.topUpPerYear ? { "Minimum top-up": row.topUpPerYear } : {}),
      total: row.total!,
    }] : [];
  }) : [];
  const splitUnreliable = (id: string): boolean => {
    const history = histories.get(id);
    return history ? perParcelByPart(history, from, to).d2c === "unreliable" : false;
  };
  const combinedIds = orderedIds.filter((id) => rates.has(id) && splitUnreliable(id));
  const unrated = completeRows.flatMap((row) => {
    const rate = rates.get(row.providerId);
    return rate?.unratedMonths.length ? [{ id: row.providerId, months: rate.unratedMonths }] : [];
  });
  const monthlyDecision = decision(rows);
  const bindingRows = completeRows.filter((row) => row.binding);
  const showMinimumTopUp = completeRows.some((row) => (row.topUpPerYear ?? 0) > 0);
  const trailingColumns = drivers ? 10 : 6;
  const labelOf = (id: string): string => providers.get(id)?.label ?? id;
  const incompleteVolumes = incompleteRows.map((row) => parcelsPerMonth(rates.get(row.providerId)!)?.toLocaleString("en-GB", { maximumFractionDigits: 0 }) ?? "0");
  const incompleteNote = incompleteRows.length === 1
    ? `${labelOf(incompleteRows[0].providerId)} is not in this comparison: ${incompleteVolumes[0]} parcels a month is too few to read a price from its bills, so a rate card is needed. Its bills are under Actuals.`
    : `${incompleteRows.map((row) => labelOf(row.providerId)).join(" and ")} are not in this comparison: ${incompleteVolumes.join(" and ")} parcels a month are too few to read a price from their bills, so a rate card is needed. Their bills are under Actuals.`;
  const incompleteChartSubtitle = incompleteRows.length
    ? `${incompleteRows.map((row) => labelOf(row.providerId)).join(" and ")}: their bills give no per-parcel price, so they cannot be priced at this work.`
    : undefined;
  const missingPartLabel = (key: "d2c" | "fba" | "other"): string => key === "d2c"
    ? "customer parcels"
    : key === "fba" ? "Amazon inbound parcels" : "other parcels";
  const missingPriceNote = incompleteRows.map((row) => {
    const parts = row.missing.map(({ key }) => missingPartLabel(key));
    return `${labelOf(row.providerId)}: ${parts.join(" and ")}`;
  }).join("; ");
  const hasMissing = (row: RankedStandardised, key: "d2c" | "fba" | "other"): boolean => row.missing.some((item) => item.key === key);
  const mutedBarSize = completeChartRows.length ? Math.max(...completeChartRows.map((row) => row.total)) * 0.04 : 1;
  const chartRows = [...completeChartRows, ...incompleteRows.map((row) => ({
    label: labelOf(row.providerId),
    "No price from its bills": mutedBarSize,
    endLabel: "No price from its bills",
  }))];
  const savingLine = legendLine("standardised", monthlyDecision ? {
    dearest: labelOf(monthlyDecision.dearestId),
    cheapest: labelOf(monthlyDecision.cheapestId),
    gapPerMonth: monthlyDecision.gapPerMonth,
    minimumLabel: bindingRows[0] ? labelOf(bindingRows[0].providerId) : undefined,
  } : {});

  return <section className="grid gap-3">
    <SectionTitle>What a year costs at the same work</SectionTitle>
    <p className="text-sm text-muted-foreground">Every 3PL priced at this work, from its own bills. Defaults: J&amp;J UK, {longMonth(from)} to {longMonth(to)}.</p>
    <div className="grid gap-4 sm:grid-cols-2">
      <label className="grid gap-1 text-sm">Customer parcels a month
        <Input data-nonmutating type="number" min={0} value={d2cRaw} onChange={(event) => setSearch("tc_d2c_m", event.target.value)} />
      </label>
      <label className="grid gap-1 text-sm">Amazon inbound parcels a month
        <Input data-nonmutating type="number" min={0} value={fbaRaw} onChange={(event) => setSearch("tc_fba_m", event.target.value)} />
      </label>
    </div>
    {invalidDrivers ? <Note>Invalid or negative parcel volume is treated as empty.</Note> : null}
    {!drivers ? <p className="text-sm text-muted-foreground">Type the monthly volumes to cost every 3PL at the same volume.</p> : null}
    <ScopeLine copy={scopeCopy(ids, providers, from, to, "monthly-average rates scaled to a year at the work above")} />
    <ChartFrame title={`Standardised cost a year by block${invoiceMode ? " (GBP)" : ""}`} titleVisible subtitle={incompleteChartSubtitle} pointCount={chartRows.length}>
      <BarChart data={chartRows} layout="vertical" margin={{ left: 12, right: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" tickFormatter={(value) => finiteMoney(Number(value), 0)} />
        <YAxis type="category" dataKey="label" width={96} />
        <Tooltip cursor={false} contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), 0)} />
        <Legend itemSorter={keepLegendBarOrder} />
        {BLOCKS.map((block, index) => <Bar key={block} dataKey={block} name={block === "Fixed" ? "Storage and subscription" : block} stackId="a" fill={CRITERIA_COLOURS[index]}>{!showMinimumTopUp && index === BLOCKS.length - 1 ? <LabelList dataKey="total" position="right" formatter={(value: unknown) => `${finiteMoney(Number(value), 0)} a year`} /> : null}</Bar>)}
        {showMinimumTopUp ? <Bar dataKey="Minimum top-up" name="Minimum top-up" stackId="a" fill={CRITERIA_COLOURS[BLOCKS.length]}><LabelList dataKey="total" position="right" formatter={(value: unknown) => `${finiteMoney(Number(value), 0)} a year`} /></Bar> : null}
        {incompleteRows.length ? <Bar dataKey="No price from its bills" name="No price from its bills" stackId="a" fill={CHART.grey4}><LabelList dataKey="endLabel" position="right" /></Bar> : null}
      </BarChart>
    </ChartFrame>
    <p data-legend className="text-sm text-muted-foreground">{savingLine}</p>
    <Accordion type="single" collapsible>
      <AccordionItem value="standardised-data"><AccordionTrigger data-nonmutating>Show the data</AccordionTrigger><AccordionContent className="grid gap-4">
    {monthlyDecision && drivers ? <Card>
      <CardHeader className="text-left">
        <CardTitle className="text-2xl font-bold">{monthlyDecision.aboutSame
          ? `${labelOf(monthlyDecision.cheapestId)} and ${labelOf(monthlyDecision.dearestId)} cost about the same (${moneyIn(monthlyDecision.gapPerMonth, "GBP", 0)} a month apart)`
          : `${labelOf(monthlyDecision.cheapestId)} costs ${moneyIn(monthlyDecision.gapPerMonth, "GBP", 0)} a month less than ${labelOf(monthlyDecision.dearestId)}`}</CardTitle>
        <CardDescription>Same work for both: {Math.round(drivers.d2c / 12).toLocaleString("en-GB")} customer parcels and {Math.round(drivers.fba / 12).toLocaleString("en-GB")} Amazon inbound parcels a month {driversTyped ? "(your volumes)." : `(J&J UK's average, ${longMonth(from)} to ${longMonth(to)}).`}{providers.get(monthlyDecision.cheapestId)?.region !== providers.get(monthlyDecision.dearestId)?.region
          ? ` ${labelOf(monthlyDecision.cheapestId)} (${providers.get(monthlyDecision.cheapestId)?.region}) and ${labelOf(monthlyDecision.dearestId)} (${providers.get(monthlyDecision.dearestId)?.region}) serve different markets, so this is a price benchmark at the same work, not a move of orders.`
          : ""}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <p className="text-sm font-medium">At {Math.round(drivers.d2c / 12).toLocaleString("en-GB")} customer parcels a month {labelOf(monthlyDecision.cheapestId)} is cheapest.{bindingRows.map((row) => ` ${labelOf(row.providerId)}'s minimum applies at this volume.`).join("")}</p>
        <ScopeLine copy={scopeCopy(ids, providers, from, to, "monthly average at the standardised work above")} />
        <Table scrollable={false} className={FIT_TABLE}>
          <TableHeader><TableRow>
            <TableHead>3PL</TableHead>
            <TableHead className="text-right">Per-parcel work a month</TableHead>
            <TableHead className="text-right">Storage and subscription a month</TableHead>
            <TableHead className="text-right">Total a month</TableHead>
          </TableRow></TableHeader>
          <TableBody>{monthlyDecision.rows.map((row) => <TableRow key={`decision-${row.providerId}`}>
            <TableCell className="font-medium">{labelOf(row.providerId)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.workPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row.fixedPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right font-semibold">{moneyIn(row.totalPerMonth, "GBP", 0)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
        <p className="text-sm">{(monthlyDecision.fixedShareOfGap ?? 0) >= 1
          ? `All of the gap is storage and subscription. That follows how much stock each 3PL holds, which is not standardised yet, so the saving holds only if ${labelOf(monthlyDecision.cheapestId)} would store the same stock for less.`
          : (monthlyDecision.fixedShareOfGap ?? 0) <= 0
            ? "All of the gap is per-parcel work, which is costed at the same volume for both."
            : monthlyDecision.fixedShareOfGap! >= 0.5
              ? `${Math.round(monthlyDecision.fixedShareOfGap! * 100)}% of the gap is storage and subscription. That follows how much stock each 3PL holds, which is not standardised yet, so the saving holds only if ${labelOf(monthlyDecision.cheapestId)} would store the same stock for less.`
              : `${Math.round((1 - monthlyDecision.fixedShareOfGap!) * 100)}% of the gap is per-parcel work, which is costed at the same volume for both.`}</p>
      </CardContent>
    </Card> : <Note>Pick at least two 3PLs that can be costed to see the saving a month.</Note>}
    <div className="grid gap-2">
      <h3 className="text-sm font-semibold text-ks-ink">What this number leaves out</h3>
      <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
        <li>Storage, goods-in and reshipments are not standardised yet: the bills carry amounts, not volumes, so storage and subscription are each 3PL's own bill (Bojan, 2 Nov).</li>
        {unrated.map(({ id, months }) => <li key={`unrated-${id}`}>{labelOf(id)}: {monthsInWords(months)}: bills with no parcel data in PFC, left out of its per-parcel prices.</li>)}
      </ul>
    </div>
    <div className="grid gap-2">
      <h3 className="text-sm font-semibold text-ks-ink">Minimum charges (from the 3 Nov call; editable)</h3>
      <ScopeLine copy={scopeCopy(ids, providers, from, to, "monthly minimum amounts")} />
      <Table scrollable={false} className={FIT_TABLE}>
        <TableHeader><TableRow><TableHead>3PL</TableHead><TableHead>Pick and pack minimum</TableHead><TableHead>Whole-invoice minimum</TableHead><TableHead>≈ £</TableHead></TableRow></TableHeader>
        <TableBody>{minimums.map((minimum) => <TableRow key={`minimum-${minimum.id}`}>
          <TableCell className="font-medium">{labelOf(minimum.id)}</TableCell>
          <TableCell><Input data-nonmutating aria-label={`${labelOf(minimum.id)} pick and pack minimum a month (${minimum.currency})`} type="number" min={0} value={minimum.pickPackRaw} onChange={(event) => setSearch(`tc_min_${minimum.id}_pp`, event.target.value)} /></TableCell>
          <TableCell><Input data-nonmutating aria-label={`${labelOf(minimum.id)} whole-invoice minimum a month (${minimum.currency})`} type="number" min={0} value={minimum.invoiceRaw} onChange={(event) => setSearch(`tc_min_${minimum.id}_inv`, event.target.value)} /></TableCell>
          <TableCell>≈ {moneyIn(minimum.pickPackGbp, "GBP", 0)} / ≈ {moneyIn(minimum.invoiceGbp, "GBP", 0)}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
      {minimums.filter((minimum) => minimum.conversionMissing).map((minimum) => <Note key={`minimum-rate-${minimum.id}`}>{labelOf(minimum.id)}: no rate to convert its minimum charges, so they are not applied.</Note>)}
      {minimums.filter((minimum) => minimum.pickPackRateMissing).map((minimum) => <Note key={`minimum-pick-pack-${minimum.id}`}>{labelOf(minimum.id)}: no itemised pick-and-pack rate, so its pick-and-pack minimum is not applied.</Note>)}
      {minimums.flatMap((minimum) => minimum.invalidLabels.map((label) => <Note key={`minimum-invalid-${minimum.id}-${label}`}>{labelOf(minimum.id)}: invalid or negative {label} minimum is treated as empty.</Note>))}
    </div>
    <ScopeLine copy={scopeCopy(ids, providers, from, to, "monthly-average rates and amounts, with yearly values scaled from them")} />
    <Table scrollable={false} className="table-fixed w-full text-xs [&_td]:px-1 [&_td]:whitespace-normal [&_th]:px-1 [&_th]:whitespace-normal">
      <TableHeader><TableRow>
        <TableHead>3PL</TableHead>
        <TableHead className="text-right">Storage and subscription a month</TableHead>
        <TableHead className="text-right">Customer parcels a month</TableHead>
        <TableHead className="text-right">Amazon inbound a month</TableHead>
        <TableHead className="text-right">Other a month</TableHead>
        {drivers ? <>
          <TableHead className="text-right">Total a month</TableHead>
          <TableHead className="text-right">Total a year</TableHead>
          <TableHead className="text-right">vs cheapest</TableHead>
          <TableHead className="text-right">Cost per parcel at this volume</TableHead>
        </> : null}
        <TableHead className="text-right">Rate per customer parcel</TableHead>
        <TableHead className="text-right">Rate per Amazon inbound parcel</TableHead>
      </TableRow></TableHeader>
      <TableBody>{orderedIds.map((id) => {
        const provider = providers.get(id);
        const history = histories.get(id);
        const rate = rates.get(id);
        const row = byId.get(id);
        const empty = emptyRowCopy(id, provider, history, rate, loading, errors, fetchTo);
        const providerRates = rate ? activityRates(rate, splitUnreliable(id)) : null;
        return <TableRow key={id}>
          <TableCell className="font-medium">{provider?.label ?? id}</TableCell>
          {empty ? <EmptyProviderCells copy={empty} columns={trailingColumns} error={errors.has(id)} /> : row && !row.complete ? <>
            <TableCell className="text-right">{moneyIn(rate!.fixedPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{hasMissing(row, "d2c") ? "—" : moneyIn(row.blocks["Customer parcels"] / 12, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{hasMissing(row, "fba") ? "—" : moneyIn(row.blocks["Amazon inbound"] / 12, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{hasMissing(row, "other") ? "—" : moneyIn(row.blocks["Other variable"] / 12, "GBP", 0)}</TableCell>
            {drivers ? <>
              <TableCell className="text-right">—</TableCell>
              <TableCell className="text-right">—</TableCell>
              <TableCell className="text-right">—</TableCell>
              <TableCell className="text-right">—</TableCell>
            </> : null}
            <TableCell className="text-right">{hasMissing(row, "d2c") ? "—" : moneyIn(providerRates?.d2c, "GBP", 2)}</TableCell>
            <TableCell className="text-right">{hasMissing(row, "fba") ? "—" : moneyIn(providerRates?.fba, "GBP", 2)}</TableCell>
          </> : <>
            <TableCell className="text-right">{moneyIn(rate!.fixedPerMonth, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row ? row.blocks["Customer parcels"] / 12 : null, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row ? row.blocks["Amazon inbound"] / 12 : null, "GBP", 0)}</TableCell>
            <TableCell className="text-right">{moneyIn(row ? row.blocks["Other variable"] / 12 : null, "GBP", 0)}</TableCell>
            {drivers ? <>
              <TableCell className="text-right font-semibold">{row?.total == null ? "—" : moneyIn(row.total / 12, "GBP", 0)}</TableCell>
              <TableCell className="text-right">{moneyIn(row?.total, "GBP", 0)}</TableCell>
              <TableCell className="text-right">{row?.vsCheapest == null ? "—" : `+${Math.round(row.vsCheapest * 100)}%`}</TableCell>
              <TableCell className="text-right">{moneyIn(row?.perParcel, "GBP", 2)}</TableCell>
            </> : null}
            <TableCell className="text-right">{providerRates?.combined ? "Not reliable (Amazon split)" : moneyIn(providerRates?.d2c, "GBP", 2)}</TableCell>
            <TableCell className="text-right">{providerRates?.combined ? "Not reliable (Amazon split)" : moneyIn(providerRates?.fba, "GBP", 2)}</TableCell>
          </>}
        </TableRow>;
      })}</TableBody>
    </Table>
    {missingPriceNote ? <Note>No price from bills — {missingPriceNote}.</Note> : null}
    {incompleteRows.length ? <Note>{incompleteNote}</Note> : null}
    {combinedIds.map((id) => <Footnote key={`combined-${id}`}>{providers.get(id)?.label ?? id}: its split between shop orders and Amazon cannot be trusted, so customer parcels and Amazon inbound are both costed at its combined rate per parcel.</Footnote>)}
      </AccordionContent></AccordionItem>
    </Accordion>
  </section>;
}

function CostTables({
  mode,
  content = "both",
  actualView = "year",
  ids,
  providers,
  histories,
  rates,
  from,
  to,
  fetchTo,
  loading,
  errors,
  gbpRates,
  invoiceMode,
}: {
  mode: "yearly" | "fixed";
  content?: "both" | "chart" | "table";
  actualView?: "year" | "per_parcel";
  ids: string[];
  providers: Map<string, TplProvider>;
  histories: Map<string, TplHistory>;
  rates: Map<string, RunRate>;
  from: string;
  to: string;
  fetchTo: string;
  loading: Set<string>;
  errors: Set<string>;
  gbpRates: Map<string, RunRate>;
  invoiceMode: boolean;
}) {
  const rows = ids.map((id) => ({ id, provider: providers.get(id), history: histories.get(id), rate: rates.get(id) }));
  type ActualChartRow = { label: string; yearly?: number | null; total?: number } & Partial<Record<(typeof PARTS)[number], number>>;
  const chartRows = ids
    .map((id) => ({ provider: providers.get(id), rate: gbpRates.get(id) }))
    .filter((row): row is { provider: TplProvider; rate: RunRate | undefined } => row.provider != null)
    .reduce<ActualChartRow[]>((result, row) => {
      const monthly = row.rate ? actualMonthly(row.rate) : null;
      const label = `${row.provider.label}${monthly != null && monthly.parcels < INDEX_FLOOR ? " · low volume" : ""}`;
      if (actualView === "year") {
        result.push({ label, ...row.rate?.yearlyParts, yearly: row.rate?.yearly ?? null });
        return result;
      }
      const values = row.rate ? actualPerParcel(row.rate) : null;
      if (values) result.push({ label, ...values });
      return result;
    }, [])
    .sort((a, b) => Number(a.yearly ?? a.total) - Number(b.yearly ?? b.total));
  const noParcelRows = actualView === "per_parcel"
    ? rows.filter(({ rate }) => rate != null && rate.monthsBilled > 0 && rate.parcels === 0)
    : [];
  const actualAxisDp = actualView === "year"
    ? 0
    : Math.max(0, ...chartRows.map((row) => Number(row.total ?? 0))) >= 10 ? 0 : 2;
  return (
    mode === "yearly" ? <>
      {content !== "table" ? <><ScopeLine copy={scopeCopy(ids, providers, from, to, actualView === "year" ? "billed-month average scaled to a year" : "summed bill lines divided by parcels in the same months")} /><ChartFrame
        title={`${actualView === "year" ? "Actual yearly cost per 3PL, by part" : "Actual cost per parcel per 3PL, by part"}${invoiceMode ? " (GBP)" : ""}`}
        subtitle={actualView === "year" ? "3PLs billed for fewer months than the range are scaled to a year." : "Each part of the bill divided by the parcels shipped in the same months."}
        titleVisible
        pointCount={chartRows.length}
      >
        <BarChart data={chartRows} layout="vertical" margin={{ left: 12, right: 12 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tickFormatter={(value) => finiteMoney(Number(value), actualAxisDp)} />
          <YAxis type="category" dataKey="label" width={96} />
          <Tooltip cursor={false} contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), actualView === "year" ? 0 : 2)} />
          <Legend itemSorter={keepLegendBarOrder} />
          {PARTS.map((part, index) => <Bar key={part} dataKey={part} stackId="a" fill={CRITERIA_COLOURS[index]} />)}
        </BarChart>
      </ChartFrame></> : null}
      {content !== "chart" ? <><ScopeLine copy={scopeCopy(ids, providers, from, to, actualView === "year" ? "billed-month average scaled to a year" : "summed bill lines divided by parcels in the same months")} /><Table scrollable={false} className={WIDE_TABLE}>
        <TableHeader><TableRow>
          <TableHead>3PL</TableHead><TableHead>Bills up to</TableHead><TableHead>Months billed</TableHead>
          <TableHead className="text-right">Parcels a year</TableHead>
          {PARTS.map((part) => <TableHead key={part} className="text-right">{part}</TableHead>)}
          <TableHead className="text-right">{actualView === "year" ? "Yearly total" : "Per parcel total"}</TableHead>
        </TableRow></TableHeader>
        <TableBody>{rows.map(({ id, provider, history, rate }) => {
          const empty = emptyRowCopy(id, provider, history, rate, loading, errors, fetchTo);
          const perParcelValues = rate ? actualPerParcel(rate) : null;
          return <TableRow key={id}>
            <TableCell className="font-medium">{provider?.label ?? id}</TableCell>
            {empty ? <EmptyProviderCells copy={empty} columns={10} error={errors.has(id)} /> : <>
              <TableCell data-label="Bills up to">{lastBilled(history!, to) ? shortMonth(lastBilled(history!, to)!) : "—"}</TableCell>
              <TableCell data-label="Months billed">{rate!.monthsBilled}</TableCell>
              <TableCell data-label="Parcels a year" className="text-right">{Math.round(rate!.parcels * 12 / rate!.monthsBilled).toLocaleString("en-GB")}</TableCell>
              {PARTS.map((part) => <TableCell key={part} data-label={part} className="text-right">{moneyIn(actualView === "year" ? rate!.yearlyParts?.[part] : perParcelValues?.[part], invoiceMode ? provider!.currency : "GBP", actualView === "year" ? 0 : 2)}</TableCell>)}
              <TableCell data-label={actualView === "year" ? "Yearly total" : "Per parcel total"} className="text-right font-semibold">{moneyIn(actualView === "year" ? rate!.yearly : perParcelValues?.total, invoiceMode ? provider!.currency : "GBP", actualView === "year" ? 0 : 2)}</TableCell>
            </>}
          </TableRow>;
        })}</TableBody>
      </Table></> : null}
      {content !== "chart" && noParcelRows.length ? <Note>{noParcelRows.map(({ id, provider }) => provider?.label ?? id).join(", ")}: no parcels in this range, so there is no cost per parcel.</Note> : null}
      {content !== "chart" ? rows.filter(({ history, rate }) => history && rate && rate.monthsBilled > 0 && lastBilled(history, to) && lastBilled(history, to)! < to).map(({ id, provider, history }) => (
        <Note key={id}>{provider!.label}: no bills after {shortMonth(lastBilled(history!, to)!)}; its year is scaled from the months billed.</Note>
      )) : null}

    </> : <section className="grid gap-3">
        <ScopeLine copy={scopeCopy(ids, providers, from, to, "billed-month average scaled to a year")} />
        <Table scrollable={false} className={FIT_TABLE}>
          <TableHeader><TableRow>
            <TableHead>3PL</TableHead><TableHead className="text-right">Fixed per year</TableHead><TableHead className="text-right">Variable per year</TableHead>
            <TableHead className="text-right">Other per year</TableHead><TableHead className="text-right">Fixed share</TableHead><TableHead className="text-right">Fixed per month</TableHead>
          </TableRow></TableHeader>
          <TableBody>{rows.map(({ id, provider, history, rate }) => {
            const empty = emptyRowCopy(id, provider, history, rate, loading, errors, fetchTo);
            return <TableRow key={id}>
              <TableCell className="font-medium">{provider?.label ?? id}</TableCell>
              {empty ? <EmptyProviderCells copy={empty} columns={5} error={errors.has(id)} /> : <>
                <TableCell className="text-right">{moneyIn(yearlyValue(rate!, ["Subscription", "Storage"]), invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
                <TableCell className="text-right">{moneyIn(yearlyValue(rate!, ["D2C shipping", "Amazon inbound", "Stock freight"]), invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
                <TableCell className="text-right">{moneyIn(yearlyValue(rate!, ["Other"]), invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
                <TableCell className="text-right">{rate!.fixedShare == null ? "—" : `${Math.round(rate!.fixedShare! * 100)}%`}</TableCell>
                <TableCell className="text-right">{moneyIn(rate!.fixedPerMonth, invoiceMode ? provider!.currency : "GBP", 0)}</TableCell>
              </>}
            </TableRow>;
          })}</TableBody>
        </Table>
        <Note>Fixed is paid even with no orders. The split is our reading of the cost types and still needs Bojan's confirmation. {invoiceMode ? "TFI's stock freight lines from Feb to May 26 are probably storage" : "TFI's stock freight lines (GBP 1,282, Feb to May 26) are probably storage"}: the v1 page counted them as fixed (TFI 99.7% fixed); here they stay variable, as the engine codes them, until Bojan confirms. Amounts here are per year; the v1 page showed totals for the months billed.</Note>
    </section>
  );
}

const YEAR_COST_HEADINGS = ["Year", "Months billed", "Parcels", "Cost", "Cost per parcel", "Change", "Storage per month", "Change", "Subscription per month"] as const;
const YEAR_PARCEL_HEADINGS = ["Year", "Pick and Pack per non-Amazon parcel", "Change", "Packaging per non-Amazon parcel", "Change", "Courier per non-Amazon parcel", "Change"] as const;

function YearTable({ history, label, to, loadedFrom, from, currency, loading, error }: {
  history: TplHistory | undefined;
  label: string;
  to: string;
  loadedFrom: string;
  from: string;
  currency: string;
  loading: boolean;
  error: boolean;
}) {
  const title = `${label} year by year since ${from.slice(0, 4)}`;
  if (loading) return <><SectionTitle>{title}</SectionTitle><Note>Loading…</Note></>;
  if (error) return <><SectionTitle>{title}</SectionTitle><Alert><AlertDescription>{ROW_ERROR}</AlertDescription></Alert></>;
  if (!history) return <><SectionTitle>{title}</SectionTitle><Note>Not in the engine yet</Note></>;
  const rows = yearRows(history, to, loadedFrom, from);
  const suspect = [...new Set(rows.flatMap((row) => row.suspectMonths))].sort();
  const flags = new Map<string, { words: string; months: string[] }>();
  history.months.filter((month) => ym(month.month) >= from && ym(month.month) <= to).forEach((month) => {
    month.flags.forEach((flag) => {
      const current = flags.get(flag.code) ?? { words: flag.words, months: [] };
      current.months.push(ym(month.month));
      flags.set(flag.code, current);
    });
  });
  const valueWithMonths = (value: number | null, count: number, billed: number) => `${moneyIn(value, currency, 0)}${count < billed ? ` (${count} months)` : ""}`;
  return (
    <section className="grid gap-3">
      <SectionTitle>{title}</SectionTitle>
      <ScopeLine copy={`${label}; ${longMonth(from)} to ${longMonth(to)}; summed by calendar year, with monthly averages where named.`} />
      <h3 className="text-sm font-semibold text-ks-ink">Costs</h3>
      <Table data-year-table="costs" scrollable={false} className={WIDE_TABLE}>
        <TableHeader><TableRow>
          {YEAR_COST_HEADINGS.map((heading, index) => <TableHead key={`${heading}-${index}`} className={index > 0 ? "text-right" : undefined}>{heading}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>{rows.map((row) => {
          const label = `${row.label}${row.loaded === "partial" ? " (part loaded)" : ""}`;
          if (row.loaded === "none" || row.monthsBilled === 0) return (
            <TableRow key={row.year}><TableCell>{label}</TableCell><TableCell colSpan={YEAR_COST_HEADINGS.length - 1} className="text-muted-foreground">{row.loaded === "none" ? "Not loaded" : "No bills"}</TableCell></TableRow>
          );
          return <TableRow key={row.year}>
            <TableCell>{label}</TableCell><TableCell data-label="Months billed" className="text-right">{row.monthsBilled}</TableCell><TableCell data-label="Parcels" className="text-right">{row.parcels.toLocaleString("en-GB")}</TableCell>
            <TableCell data-label="Cost" className="text-right">{moneyIn(row.cost, currency, 0)}</TableCell><TableCell data-label="Cost per parcel" className="text-right">{moneyIn(row.costPerParcel, currency, 2)}</TableCell><TableCell data-label="Change" className="text-right">{percent(row.costPerParcelChange, row.changeSuspect)}</TableCell>
            <TableCell data-label="Storage per month" className="text-right">{valueWithMonths(row.storagePerMonth, row.storageMonths, row.monthsBilled)}</TableCell><TableCell data-label="Change" className="text-right">{percent(row.storagePerMonthChange, row.changeSuspect)}</TableCell>
            <TableCell data-label="Subscription per month" className="text-right">{valueWithMonths(row.subscriptionPerMonth, row.subscriptionMonths, row.monthsBilled)}</TableCell>
          </TableRow>;
        })}</TableBody>
      </Table>
      <h3 className="text-sm font-semibold text-ks-ink">Per non-Amazon parcel</h3>
      <Table data-year-table="parcels" scrollable={false} className={WIDE_TABLE}>
        <TableHeader><TableRow>
          {YEAR_PARCEL_HEADINGS.map((heading, index) => <TableHead key={`${heading}-${index}`} className={index > 0 ? "text-right" : undefined}>{heading}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>{rows.map((row) => {
          const label = `${row.label}${row.loaded === "partial" ? " (part loaded)" : ""}`;
          if (row.loaded === "none" || row.monthsBilled === 0) return (
            <TableRow key={row.year}><TableCell>{label}</TableCell><TableCell colSpan={YEAR_PARCEL_HEADINGS.length - 1} className="text-muted-foreground">{row.loaded === "none" ? "Not loaded" : "No bills"}</TableCell></TableRow>
          );
          return <TableRow key={row.year}>
            <TableCell>{label}</TableCell>
            <TableCell data-label="Pick and Pack" className="text-right">{moneyIn(row.perNonFba.pick_pack, currency, 2)}</TableCell><TableCell data-label="Change" className="text-right">{percent(row.perNonFbaChange.pick_pack, row.changeSuspect)}</TableCell>
            <TableCell data-label="Packaging" className="text-right">{moneyIn(row.perNonFba.packaging, currency, 2)}</TableCell><TableCell data-label="Change" className="text-right">{percent(row.perNonFbaChange.packaging, row.changeSuspect)}</TableCell>
            <TableCell data-label="Courier" className="text-right">{moneyIn(row.perNonFba.courier, currency, 2)}</TableCell><TableCell data-label="Change" className="text-right">{percent(row.perNonFbaChange.courier, row.changeSuspect)}</TableCell>
          </TableRow>;
        })}</TableBody>
      </Table>
      {rows.some((row) => row.changeSuspect) ? <Footnote>* The year or the year before includes a month whose bill detail does not tie ({monthsInWords(suspect)}), so the change may reflect billing rather than a price rise.</Footnote> : null}
      {[...flags.entries()].map(([code, flag]) => <Footnote key={code}>{flag.words} ({monthsInWords(flag.months)})</Footnote>)}
    </section>
  );
}

const PER_PARCEL_ROWS: Array<{ key: PerParcelKey; label: string }> = [
  { key: "pick_pack", label: "Pick and Pack" }, { key: "packaging", label: "Packaging" }, { key: "courier", label: "Courier" },
  { key: "d2c", label: "All D2C shipping (per non-Amazon parcel)" }, { key: "d2c_amazon", label: "D2C plus Amazon inbound (per parcel, all parcels)" },
];
const perParcelLabel = (value: PerParcel, currency: string): string => value === "not_itemised" ? "Not itemised" : value === "unreliable" ? "Not reliable (Amazon split)" : moneyIn(value, currency, 2);

function ComparisonTable({ ids, providers, histories, gbpHistories, loading, errors, from, to, defaultComparison, invoiceMode }: {
  ids: string[];
  providers: Map<string, TplProvider>;
  histories: Map<string, TplHistory>;
  gbpHistories: Map<string, TplHistory>;
  loading: Set<string>;
  errors: Set<string>;
  from: string;
  to: string;
  defaultComparison: boolean;
  invoiceMode: boolean;
}) {
  const columns = ids.map((id) => ({ id, provider: providers.get(id), history: histories.get(id) }));
  const labels = columns.map(({ id, provider }) => provider?.label ?? id);
  const values = columns.map(({ history }) => history ? perParcelByPart(history, from, to) : null);
  const gbpValues = ids.map((id) => gbpHistories.get(id)).map((history) => history ? perParcelByPart(history, from, to) : null);
  const showDifference = columns.length === 2;
  const sameInvoiceCurrency = !invoiceMode || (columns[0]?.provider?.currency != null && columns[0].provider.currency === columns[1]?.provider?.currency);
  const title = showDifference ? `Cost per parcel by part: ${labels[0]} vs ${labels[1]}` : "Cost per parcel by part";
  return <section className="grid gap-3">
    <SectionTitle>{title}</SectionTitle>
    <ScopeLine copy={scopeCopy(ids, providers, from, to, "summed bill lines divided by parcels in the same months")} />
    <Table data-transposed scrollable={false} className={FIT_TABLE}><TableHeader><TableRow><TableHead>3PL</TableHead>
      {PER_PARCEL_ROWS.map(({ key, label }) => <TableHead key={key} className="text-right">{label}</TableHead>)}
    </TableRow></TableHeader>
      <TableBody>
        {columns.map(({ id, provider, history }, index) => <TableRow key={id}><TableCell className="font-medium">{labels[index]}</TableCell>
          {PER_PARCEL_ROWS.map(({ key }) => <TableCell key={key} className="text-right">
            {!provider ? "Not in the engine yet" : loading.has(id) ? "Loading…" : errors.has(id) ? <span role="alert">{ROW_ERROR}</span> : history ? perParcelLabel(values[index]?.[key] ?? null, invoiceMode ? provider.currency : "GBP") : "No bills in this range"}
          </TableCell>)}
        </TableRow>)}
        {showDifference ? <TableRow><TableCell className="font-medium">{`${defaultComparison ? "Difference" : `${labels[1]} minus ${labels[0]}`}${invoiceMode ? " (GBP)" : ""}`}</TableCell>
          {PER_PARCEL_ROWS.map(({ key }) => {
            const gbpA = gbpValues[0]?.[key] ?? null; const gbpB = gbpValues[1]?.[key] ?? null;
            const difference = sameInvoiceCurrency && typeof gbpA === "number" && typeof gbpB === "number" ? gbpB - gbpA : null;
            return <TableCell key={key} className="text-right">{finiteMoney(difference)}</TableCell>;
          })}
        </TableRow> : null}
      </TableBody>
    </Table>
    <Note>Per parcel over the months with parcels. Where the 3PL's split between shop orders and Amazon cannot be trusted (a negative part, or the engine's Amazon-gap flag on half the months or more), only the last row compares like for like.</Note>
  </section>;
}

function WhatIf({
  histories, rates, from, to, receivers, receiving, showMove, showAged, extraRaw, agedRaw, setSearch, invoiceMode,
}: {
  histories: Map<string, TplHistory>; rates: Map<string, RunRate>; from: string; to: string;
  receivers: ProviderId[]; receiving: ProviderId; showMove: boolean; showAged: boolean;
  extraRaw: string; agedRaw: string; setSearch: (key: string, value: string) => void;
  invoiceMode: boolean;
}) {
  const receivingHistory = histories.get(receiving);
  const receivingD2c = receivingHistory ? perParcelByPart(receivingHistory, from, to).d2c : null;
  const tfi = rates.get("tfi"); const jjUk = rates.get("jj_uk");
  const extra = inputNumber(extraRaw); const aged = inputNumber(agedRaw);
  const move = tfi ? moveTfiSaving(tfi, receivingD2c, extra) : null;
  const clear = jjUk ? agedStockSaving(jjUk, aged) : null;
  return <section className="grid gap-3">
    <SectionTitle>{`What if${invoiceMode ? " (GBP)" : ""}`}</SectionTitle>
    <div className="grid gap-4 lg:grid-cols-2">
      {showMove ? <Card><CardHeader><CardTitle>Move TFI orders</CardTitle><CardDescription>TFI's non-Amazon orders are costed at the receiving 3PL's D2C cost per parcel; its Amazon parcels are not costed.</CardDescription></CardHeader><CardContent className="grid gap-4">
        <label className="grid gap-1 text-sm">Move TFI orders to
          <Select value={receiving} onValueChange={(value) => setSearch("tc_rcv", value)}><SelectTrigger data-nonmutating><SelectValue /></SelectTrigger><SelectContent>{receivers.map((id) => <SelectItem key={id} value={id}>{histories.get(id)?.provider.label ?? id}</SelectItem>)}</SelectContent></Select>
        </label>
        <label className="grid gap-1 text-sm">Extra freight to the UAE per year (GBP)<Input data-nonmutating type="number" min={0} value={extraRaw} onChange={(event) => setSearch("tc_extra", event.target.value)} /></label>
        <p className="font-medium">{!tfi || !receivingHistory ? "Not enough data to work this out." : extraRaw === "" ? "Type the extra freight to the UAE to see the saving." : move == null ? "Not enough data to work this out." : <>Saving per year, before delivery inside the UAE (upper bound): {finiteMoney(move, 0)}</>}</p>
      </CardContent></Card> : <Note>Choose TFI and J&amp;J UK or Omnipack in the 3PLs filter to use this what-if.</Note>}
      {showAged ? <Card><CardHeader><CardTitle>Clear J&amp;J UK aged stock</CardTitle><CardDescription>Take the aged share from J&amp;J's latest storage report.</CardDescription></CardHeader><CardContent className="grid gap-4">
        <label className="grid gap-1 text-sm">Aged stock share of J&amp;J UK storage (%)<Input data-nonmutating type="number" min={0} max={100} value={agedRaw} onChange={(event) => setSearch("tc_aged", event.target.value)} /></label>
        <p className="font-medium">{clear == null ? "Type the aged share to see the saving." : <>Saving per year: {finiteMoney(clear, 0)}</>}</p>
      </CardContent></Card> : <Note>Choose J&amp;J UK in the 3PLs filter to use this what-if.</Note>}
    </div>
    {showMove && showAged && move != null && clear != null ? <p className="text-lg font-semibold">Both together: {finiteMoney(move + clear, 0)}</p> : null}
  </section>;
}

export function TplCostModelPage(_props: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const fetchTo = lastCompleteMonth(new Date());
  const loadedFrom = fetchFrom(fetchTo);
  const providersQuery = useQuery({ queryKey: [...QUERY_ROOT, "providers"], queryFn: getTplProviders, staleTime: 60_000 });
  const providers = providersQuery.data?.data.providers ?? [];
  const providersById = providerMap(providers);
  const allCompanyOptions = companyOptions(providers, []);
  const companies = parseList(search.tc_co).filter((company) => allCompanyOptions.includes(company));
  const availableRegions = regionOptions(providers, companies);
  const regions = parseList(search.tc_rg).filter((region) => availableRegions.includes(region));
  const availableCompanies = companyOptions(providers, regions);
  const options = selectableProviders(providers, companies, regions);
  const picked = parseList(search.tc_pv);
  const filtered = companies.length > 0 || regions.length > 0;
  const chosen = chosenIds(options, picked, filtered);
  const invoiceMode = search.tc_cur === "invoice";
  const currentServed = CURRENT_3PLS.filter((id) => providersById.has(id));
  const fetchIds = [...new Set([...currentServed, ...chosen.filter((id) => providersById.has(id))])];
  const historyQueries = useQueries({ queries: fetchIds.map((id) => ({
    queryKey: [...QUERY_ROOT, "history", id, loadedFrom, fetchTo],
    queryFn: () => getTplHistory({ provider: id, from: loadedFrom, to: fetchTo, currency: "gbp" }),
    staleTime: 60_000,
  })) });
  const invoiceHistoryQueries = useQueries({ queries: fetchIds.map((id) => ({
    queryKey: [...QUERY_ROOT, "history", id, loadedFrom, fetchTo, "invoice"],
    queryFn: () => getTplHistory({ provider: id, from: loadedFrom, to: fetchTo, currency: "invoice" }),
    enabled: invoiceMode || providersById.get(id)?.currency !== "GBP",
    staleTime: 60_000,
  })) });
  const loadedGbpHistories = historyQueries.flatMap((query) => query.data?.data ? [query.data.data] : []);
  const loadedInvoiceHistories = invoiceHistoryQueries.flatMap((query) => query.data?.data ? [query.data.data] : []);
  const gbpHistories = new Map(loadedGbpHistories.map((history) => [history.provider.provider_id, history]));
  const invoiceHistories = new Map(loadedInvoiceHistories.map((history) => [history.provider.provider_id, history]));
  const histories = invoiceMode ? invoiceHistories : gbpHistories;
  const baselineHistories = currentServed.flatMap((id) => gbpHistories.get(id) ? [gbpHistories.get(id)!] : []);
  const effectiveDefault = defaultTo(baselineHistories, fetchTo);
  const to = validTo(search.tc_to, fetchTo) ?? effectiveDefault;
  const from = validFrom(search.tc_from, to, fetchTo) ?? defaultFrom(to, fetchTo);
  const gbpRates = new Map(loadedGbpHistories.map((history) => [history.provider.provider_id, runRate(history, from, to)]));
  const invoiceRates = new Map(loadedInvoiceHistories.map((history) => [history.provider.provider_id, runRate(history, from, to)]));
  const rates = invoiceMode ? invoiceRates : gbpRates;
  const setSearch = (key: string, value: string) => void navigate({ search: ((previous: Record<string, unknown>) => {
    const next = { ...(previous as Record<string, unknown>) };
    if (value === "") delete next[key]; else next[key] = value;
    return next;
  }) as never } as never);
  const setLinkedFilter = (key: "tc_co" | "tc_rg", values: string[]) => void navigate({ search: ((previous: Record<string, unknown>) => {
    const next = { ...(previous as Record<string, unknown>) };
    const nextCompanies = (key === "tc_co" ? values : companies).filter((company) => allCompanyOptions.includes(company));
    const nextRegionOptions = regionOptions(providers, nextCompanies);
    const nextRegions = (key === "tc_rg" ? values : parseList(previous.tc_rg)).filter((region) => nextRegionOptions.includes(region));
    if (nextCompanies.length) next.tc_co = nextCompanies.join(","); else delete next.tc_co;
    if (nextRegions.length) next.tc_rg = nextRegions.join(","); else delete next.tc_rg;
    return next;
  }) as never } as never);
  const queryById = new Map(fetchIds.map((id, index) => [id, historyQueries[index]]));
  const invoiceQueryById = new Map(fetchIds.map((id, index) => [id, invoiceHistoryQueries[index]]));
  const displayQueryById = invoiceMode ? invoiceQueryById : queryById;
  const baselineErrors = currentServed.map((id) => queryById.get(id)?.error).filter(Boolean);
  const failure = providersQuery.error ?? baselineErrors[0];
  const status = [providersQuery.error, ...baselineErrors].some((error) => errorStatus(error) === 403)
    ? 403 : errorStatus(providersQuery.error) === 404 ? 404 : null;
  const pending = providersQuery.isPending || currentServed.some((id) => queryById.get(id)?.isPending);
  const loading = new Set(chosen.filter((id) => displayQueryById.get(id)?.isPending));
  const errors = new Set(chosen.filter((id) => displayQueryById.get(id)?.isError));
  const gbpLoading = new Set(chosen.filter((id) => queryById.get(id)?.isPending));
  const gbpErrors = new Set(chosen.filter((id) => queryById.get(id)?.isError));
  const view = search.tc_view === "per_parcel" ? "per_parcel" : "total";
  const actualView = search.tc_actual === "per_parcel" ? "per_parcel" : "year";
  const metric: IndexMetric = INDEX_METRICS.some(({ value }) => value === search.tc_metric) ? search.tc_metric as IndexMetric : "per_parcel";
  const receivers = (["jj_uk", "omnipack"] as ProviderId[]).filter((id) => chosen.includes(id));
  const receiving: ProviderId = typeof search.tc_rcv === "string" && receivers.includes(search.tc_rcv as ProviderId)
    ? search.tc_rcv as ProviderId
    : receivers[0] ?? "jj_uk";
  const showMove = chosen.includes("tfi") && receivers.length > 0;
  const showAged = chosen.includes("jj_uk");
  const yearIds = chosen.filter((id) => providersById.has(id));
  const yearId = typeof search.tc_yr === "string" && yearIds.includes(search.tc_yr) ? search.tc_yr : yearIds[0];
  const extraRaw = typeof search.tc_extra === "string" ? search.tc_extra : "";
  const agedRaw = typeof search.tc_aged === "string" ? search.tc_aged : "";
  const driverDefaults = defaultDrivers(gbpRates.get("jj_uk"));
  const jjUkRate = gbpRates.get("jj_uk");
  const monthlyRaw = (monthlyKey: string, yearlyKey: string, monthlyDefault: number | undefined): string => {
    if (typeof search[monthlyKey] === "string") return search[monthlyKey];
    if (typeof search[yearlyKey] === "string") {
      const yearly = inputNumber(search[yearlyKey]);
      return yearly == null ? String(search[yearlyKey]) : String(yearly / 12);
    }
    return monthlyDefault == null ? "" : Math.round(monthlyDefault).toString();
  };
  const d2cMonthlyDefault = jjUkRate && jjUkRate.monthsBilled > 0 ? jjUkRate.nonFbaParcels / jjUkRate.monthsBilled : driverDefaults?.d2c == null ? undefined : driverDefaults.d2c / 12;
  const fbaMonthlyDefault = jjUkRate && jjUkRate.monthsBilled > 0 ? (jjUkRate.parcels - jjUkRate.nonFbaParcels) / jjUkRate.monthsBilled : driverDefaults?.fba == null ? undefined : driverDefaults.fba / 12;
  const d2cRaw = monthlyRaw("tc_d2c_m", "tc_d2c", d2cMonthlyDefault);
  const fbaRaw = monthlyRaw("tc_fba_m", "tc_fba", fbaMonthlyDefault);
  const parsedD2c = inputNumber(d2cRaw);
  const parsedFba = inputNumber(fbaRaw);
  const invalidDrivers = [d2cRaw, fbaRaw].some((raw) => raw.trim() !== "" && (inputNumber(raw) == null || Number(raw) < 0));
  const driverCandidate = parsedD2c == null || parsedFba == null ? null : { d2c: parsedD2c * 12, fba: parsedFba * 12 };
  const drivers = validDrivers(driverCandidate) ? driverCandidate : null;
  const driversTyped = typeof search.tc_d2c_m === "string" || typeof search.tc_fba_m === "string"
    || typeof search.tc_d2c === "string" || typeof search.tc_fba === "string";
  const minimumConversionSources = providers.map((provider) => ({
    providerId: provider.provider_id,
    currency: provider.currency,
    gbp: gbpHistories.get(provider.provider_id),
    invoice: invoiceHistories.get(provider.provider_id),
  }));
  const minimums: ProviderMinimum[] = chosen.map((id) => {
    const provider = providersById.get(id);
    const agreed = minimumDefault(id);
    const defaultKind = id === "omnipack" ? "inv" : "pp";
    const billCurrency = provider?.currency ?? "GBP";
    const currency = agreed?.currency ?? billCurrency;
    const pickPackKey = `tc_min_${id}_pp`;
    const invoiceKey = `tc_min_${id}_inv`;
    const pickPackRaw = typeof search[pickPackKey] === "string" ? search[pickPackKey] as string : agreed && defaultKind === "pp" ? String(agreed.amount) : "";
    const invoiceRaw = typeof search[invoiceKey] === "string" ? search[invoiceKey] as string : agreed && defaultKind === "inv" ? String(agreed.amount) : "";
    const parseMinimum = (raw: string): number | null => {
      const value = inputNumber(raw);
      return value != null && value >= 0 ? value : null;
    };
    const pickPackAmount = parseMinimum(pickPackRaw);
    const invoiceAmount = parseMinimum(invoiceRaw);
    const conversion = minimumToGbpRatio(id, billCurrency, currency, minimumConversionSources, from, to);
    const pickPackPart = gbpHistories.get(id) ? itemisedPickPackRate(gbpHistories.get(id)!, from, to) : null;
    return {
      id,
      currency,
      pickPackRaw,
      invoiceRaw,
      pickPackGbp: pickPackAmount == null || conversion == null ? null : pickPackAmount * conversion,
      invoiceGbp: invoiceAmount == null || conversion == null ? null : invoiceAmount * conversion,
      conversionMissing: conversion == null && (pickPackAmount != null || invoiceAmount != null),
      pickPackRateMissing: pickPackAmount != null && conversion != null && pickPackPart == null,
      invalidLabels: [
        ...(pickPackRaw.trim() !== "" && pickPackAmount == null ? ["pick-and-pack"] : []),
        ...(invoiceRaw.trim() !== "" && invoiceAmount == null ? ["whole-invoice"] : []),
      ],
    };
  });
  const minimumsById = new Map(minimums.map((minimum) => [minimum.id, minimum]));
  const standardisedRows = drivers ? rankStandardised(chosen.flatMap((id) => {
    const rate = gbpRates.get(id);
    const history = gbpHistories.get(id);
    if (!rate || !history) return [];
    const splitUnreliable = perParcelByPart(history, from, to).d2c === "unreliable";
    const row = standardised(rate, drivers, splitUnreliable);
    const minimum = minimumsById.get(id);
    const pickPackPart = itemisedPickPackRate(history, from, to);
    return [withMinimums(row, parsedD2c!, pickPackPart, {
      pickPack: minimum?.pickPackRateMissing ? null : minimum?.pickPackGbp ?? null,
      invoice: minimum?.invoiceGbp ?? null,
    })];
  })) : [];
  const selectedHistories = chosen.flatMap((id) => histories.get(id) ? [histories.get(id)!] : []);
  const selectedGbpHistories = chosen.flatMap((id) => gbpHistories.get(id) ? [gbpHistories.get(id)!] : []);
  const trends = trendRows(selectedHistories, from, to);
  const gbpTrends = trendRows(selectedGbpHistories, from, to);
  const belowFloorIds = belowIndexFloor(selectedGbpHistories, from, to);
  const indices = indexRows(selectedGbpHistories, from, to, metric);
  const indexGapIds = indexGaps(selectedGbpHistories, from, to, metric);
  const metricLabel = INDEX_METRICS.find(({ value }) => value === metric)!.label;
  const indexNoLineReasons = new Map<string, string>();
  for (const history of selectedGbpHistories) {
    const id = history.provider.provider_id;
    // A thin 3PL with two or more index points keeps its dashed line (A13: thin ones are
    // labelled, never hidden); only a 3PL the chart cannot draw gets "no line".
    const points = indices.filter((row) => Number.isFinite((row as Record<string, unknown>)[id] as number)).length;
    if (points >= 2) continue;
    const billed = history.months.filter((month) => {
      const key = ym(month.month);
      return key >= from && key <= to && isBilled(id, month);
    });
    if (billed.length > 0 && billed.every((month) => month.parcels.total < INDEX_FLOOR)) {
      indexNoLineReasons.set(id, `fewer than ${INDEX_FLOOR} parcels in every month`);
    } else if (indexGapIds.includes(id)) {
      indexNoLineReasons.set(id, `no ${metricLabel.toLowerCase()} in this range`);
    }
  }
  const indexNoLineIds = [...indexNoLineReasons.keys()];
  const indexChartRows = indices.map((row) => ({
    ...row,
    ...Object.fromEntries(indexNoLineIds.flatMap((id) => [[id, null], [`${id}_v`, null]])),
  }));
  const suspectRates = chosen.flatMap((id) => {
    const rate = rates.get(id); const provider = providersById.get(id);
    return rate && provider && rate.splitSuspectMonths.length ? [{ rate, provider }] : [];
  });
  const defaultComparison = !filtered && !options.some((provider) => picked.includes(provider.provider_id));
  const comparisonIds = defaultComparison ? ["jj_uk", "omnipack"] : chosen;
  const storageLeader = chosen
    .flatMap((id) => {
      const monthly = gbpRates.get(id) ? actualMonthly(gbpRates.get(id)!) : null;
      return monthly && monthly.storage > 0 ? [{ id, monthly, share: monthly.billed === 0 ? 0 : monthly.storage / monthly.billed }] : [];
    })
    .sort((a, b) => b.share - a.share)[0];
  const actualLegend = legendLine("actual", {
    from: longMonth(from),
    to: longMonth(to),
    provider: storageLeader ? providersById.get(storageLeader.id)?.label ?? storageLeader.id : undefined,
    storagePerMonth: storageLeader?.monthly.storage,
    parcelsPerMonth: storageLeader?.monthly.parcels,
  });

  return <div className="grid gap-8">
    <PageHead title="3PL History" sub={invoiceMode ? "What our chosen 3PLs cost at the same volume (standardised) and as billed (actual), from the bills in Precision Fulfilment's 3PL History (each 3PL's invoice currency; totals in GBP), for the months you pick. Yearly amounts are scaled from the months billed." : "What our chosen 3PLs cost at the same volume (standardised) and as billed (actual), from the bills in Precision Fulfilment's 3PL History (GBP), for the months you pick. Yearly amounts are scaled from the months billed."} kpi={null} />
    {failure ? <Failure status={status} /> : pending ? <Pending /> : <>
      <div className="flex flex-wrap items-end gap-4">
        <MultiPick label="Company" options={availableCompanies.map((value) => ({ value, label: COMPANY_LABEL[value] }))} value={companies.filter((company) => availableCompanies.includes(company))} onChange={(next) => setLinkedFilter("tc_co", next)} />
        <MultiPick label="Region" options={availableRegions.map((value) => ({ value, label: value }))} value={regions} onChange={(next) => setLinkedFilter("tc_rg", next)} />
        <MultiPick label="3PLs" options={options.map((provider) => ({ value: provider.provider_id, label: provider.label }))} value={options.map((provider) => provider.provider_id).filter((id) => picked.includes(id))} onChange={(next) => setSearch("tc_pv", next.join(","))} />
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="grid gap-1 text-sm">From<Input data-nonmutating type="month" min={loadedFrom} max={to} value={from} onChange={(event) => setSearch("tc_from", event.target.value)} /></label>
        <label className="grid gap-1 text-sm">To<Input data-nonmutating type="month" min={from} max={fetchTo} value={to} onChange={(event) => setSearch("tc_to", event.target.value)} /></label>
        <FilterBar layout="segmented" filters={[{ name: "tc_cur", label: "Currency", kind: "choice", options: [{ value: "invoice", label: "Invoice" }, { value: "gbp", label: "GBP" }] }]} values={{ tc_cur: invoiceMode ? "invoice" : "gbp" }} onChange={(next) => setSearch("tc_cur", next.tc_cur === "invoice" ? "invoice" : "")} />
        <p className="pb-2 text-sm text-muted-foreground">Bills loaded to {shortMonth(effectiveDefault)}</p>
      </div>
      {invoiceMode ? <Note>Per-3PL amounts are in each 3PL's invoice currency, as billed. Totals and comparisons across 3PLs stay in GBP.</Note> : null}
      {chosen.length === 0 ? <Note>No 3PL matches these filters.</Note> : <>
        <section className="grid gap-3">
          <SectionTitle>What we actually paid</SectionTitle>
          <FilterBar layout="segmented" filters={[{ name: "tc_actual", label: "Values", kind: "choice", options: [{ value: "year", label: "Total" }, { value: "per_parcel", label: "Per parcel" }] }]} values={{ tc_actual: actualView }} onChange={(next) => setSearch("tc_actual", next.tc_actual === "per_parcel" ? "per_parcel" : "")} />
          <CostTables mode="yearly" content="chart" actualView={actualView} ids={chosen} providers={providersById} histories={histories} rates={rates} from={from} to={to} fetchTo={fetchTo} loading={loading} errors={errors} gbpRates={gbpRates} invoiceMode={invoiceMode} />
          <p data-legend className="text-sm text-muted-foreground">{actualLegend}</p>
          <Accordion type="single" collapsible>
            <AccordionItem value="actual-data"><AccordionTrigger data-nonmutating>Show the data</AccordionTrigger><AccordionContent className="grid gap-4">
              <AllProvidersToday ids={chosen} providers={providersById} rates={rates} from={from} to={to} />
              <section className="grid gap-3"><SectionTitle>Actual yearly cost per 3PL, by part</SectionTitle><CostTables mode="yearly" content="table" actualView={actualView} ids={chosen} providers={providersById} histories={histories} rates={rates} from={from} to={to} fetchTo={fetchTo} loading={loading} errors={errors} gbpRates={gbpRates} invoiceMode={invoiceMode} /></section>
              <section className="grid gap-3"><SectionTitle>Actual fixed vs variable</SectionTitle><CostTables mode="fixed" ids={chosen} providers={providersById} histories={histories} rates={rates} from={from} to={to} fetchTo={fetchTo} loading={loading} errors={errors} gbpRates={gbpRates} invoiceMode={invoiceMode} /></section>
              <section className="grid gap-3"><SectionTitle>{yearId ? `${providersById.get(yearId)?.label ?? yearId} year by year since ${from.slice(0, 4)}` : "3PL year by year"}</SectionTitle>
                {yearId ? <><label className="grid w-fit gap-1 text-sm">3PL<Select value={yearId} onValueChange={(value) => setSearch("tc_yr", value)}><SelectTrigger data-nonmutating><SelectValue /></SelectTrigger><SelectContent>{yearIds.map((id) => <SelectItem key={id} value={id}>{providersById.get(id)?.label ?? id}</SelectItem>)}</SelectContent></Select></label><YearTable history={histories.get(yearId)} label={providersById.get(yearId)?.label ?? yearId} to={to} loadedFrom={loadedFrom} from={from} currency={invoiceMode ? providersById.get(yearId)?.currency ?? "GBP" : "GBP"} loading={displayQueryById.get(yearId)?.isPending === true} error={displayQueryById.get(yearId)?.isError === true} /></> : <Note>Choose a served 3PL to see its year-by-year costs.</Note>}
              </section>
              <ComparisonTable ids={comparisonIds} providers={providersById} histories={histories} gbpHistories={gbpHistories} loading={loading} errors={errors} from={from} to={to} defaultComparison={defaultComparison} invoiceMode={invoiceMode} />
              <WhatIf histories={gbpHistories} rates={gbpRates} from={from} to={to} receivers={receivers} receiving={receiving} showMove={showMove} showAged={showAged} extraRaw={extraRaw} agedRaw={agedRaw} setSearch={setSearch} invoiceMode={invoiceMode} />
              <section className="grid gap-3"><SectionTitle>Actual monthly cost per 3PL</SectionTitle>
                <FilterBar layout="segmented" filters={[{ name: "tc_view", label: "Values", kind: "choice", options: [{ value: "total", label: "Total" }, { value: "per_parcel", label: "Per parcel" }] }]} values={{ tc_view: view }} onChange={(next) => setSearch("tc_view", next.tc_view === "per_parcel" ? "per_parcel" : "")} />
                <ScopeLine copy={scopeCopy(chosen, providersById, from, to, view === "total" ? "summed each month" : "monthly bill total divided by parcels in that month")} />
                {view === "total" ? <><ChartFrame title={`Actual monthly cost per 3PL${invoiceMode ? " (GBP)" : ""}`} titleVisible pointCount={selectedGbpHistories.length ? gbpTrends.length : 0}><LineChart data={gbpTrends} margin={{ left: 8, right: 8 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tickFormatter={shortMonth} /><YAxis width={72} tickFormatter={(value) => finiteMoney(Number(value), 0)} /><Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => finiteMoney(Number(value), 0)} /><Legend />{chosen.filter((id) => gbpHistories.has(id)).map((id) => <Line key={id} type="monotone" dataKey={id} name={providersById.get(id)?.label ?? id} stroke={lineColour(id, chosen)} connectNulls={false} dot={false} />)}</LineChart></ChartFrame><p data-legend className="text-sm text-muted-foreground">As billed each month. A gap means there was no bill.</p></> : <><div className="grid gap-4 md:grid-cols-2">{chosen.filter((id) => histories.has(id)).map((id) => { const label = providersById.get(id)?.label ?? id; const axisDp = Math.max(0, ...trends.map((row) => Number(row[`${id}_pp`] ?? 0))) >= 10 ? 0 : 2; return <div className="grid gap-2" key={id}><ChartFrame title={label} titleVisible pointCount={trends.length}><LineChart data={trends} margin={{ left: 8, right: 8 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" tickFormatter={shortMonth} /><YAxis width={72} tickFormatter={(value) => moneyIn(Number(value), invoiceMode ? providersById.get(id)?.currency ?? "GBP" : "GBP", axisDp)} /><Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value) => moneyIn(Number(value), invoiceMode ? providersById.get(id)?.currency ?? "GBP" : "GBP", 2)} /><Line type="monotone" dataKey={`${id}_pp`} name={label} stroke={lineColour(id, chosen)} connectNulls={false} dot={{ r: 2 }} /></LineChart></ChartFrame><p data-legend className="text-sm text-muted-foreground">Cost per parcel each month. A gap means no bill or no parcels.</p></div>; })}</div><Note>Each 3PL has its own scale. A gap is a month with no bill or no parcels.</Note></>}
              </section>
              {suspectRates.length ? <Alert><AlertDescription>
                <p>In these months the bill's detail does not tie to its total, so the split by part above may be off; the month total is still the bill (DEC-1231).</p>
                {suspectRates.map(({ provider, rate }) => <p key={provider.provider_id}>{provider.label}: {rate.splitSuspectMonths.length} month{rate.splitSuspectMonths.length === 1 ? "" : "s"}, {monthsInWords(rate.splitSuspectMonths)}</p>)}
              </AlertDescription></Alert> : null}
            </AccordionContent></AccordionItem>
          </Accordion>
        </section>
        <StandardisedCostTable ids={chosen} providers={providersById} histories={gbpHistories} rates={gbpRates} loading={gbpLoading} errors={gbpErrors} fetchTo={fetchTo} from={from} to={to} drivers={drivers} driversTyped={driversTyped} d2cRaw={d2cRaw} fbaRaw={fbaRaw} rows={standardisedRows} minimums={minimums} invalidDrivers={invalidDrivers} setSearch={setSearch} invoiceMode={invoiceMode} />
        <section className="grid gap-3">
          <SectionTitle>Who got dearer</SectionTitle>
          <FilterBar layout="segmented" filters={[{ name: "tc_metric", label: "Metric", kind: "choice", options: [...INDEX_METRICS] }]} values={{ tc_metric: metric }} onChange={(next) => setSearch("tc_metric", next.tc_metric === "per_parcel" ? "" : String(next.tc_metric ?? ""))} />
          <ScopeLine copy={scopeCopy(chosen, providersById, from, to, "monthly values indexed to each 3PL's first billed month")} />
          <ChartFrame title={`Cost index per 3PL, ${metricLabel} (first billed month = 100)`} titleVisible pointCount={selectedGbpHistories.length ? indices.length : 0}>
            <LineChart data={indexChartRows} margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={shortMonth} />
              <YAxis width={48} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value, _name, item) => {
                const payload = item.payload as Record<string, number | string | null>;
                const raw = payload[`${String(item.dataKey)}_v`];
                return `${Number(value)} (${finiteMoney(typeof raw === "number" ? raw : null, 2)})`;
              }} />
              <Legend />
              {chosen.filter((id) => gbpHistories.has(id)).map((id) => {
                const provider = providersById.get(id);
                const noLine = indexNoLineIds.includes(id);
                return <Line key={id} type="monotone" dataKey={id} name={noLine ? `${provider?.label ?? id} (no line)` : `${provider?.label ?? id} (${provider?.region ?? "unknown region"})`} stroke={noLine ? CHART.grey4 : lineColour(id, chosen)} strokeDasharray={!noLine && belowFloorIds.includes(id) ? "4 4" : undefined} connectNulls={false} dot={false} />;
              })}
            </LineChart>
          </ChartFrame>
          <p data-legend className="text-sm text-muted-foreground">{legendLine("index")}</p>
          {[...indexNoLineReasons].map(([id, reason]) => <Note key={`index-no-line-${id}`}>{providersById.get(id)?.label ?? id}: no line, {reason}.</Note>)}
          <Accordion type="single" collapsible>
            <AccordionItem value="index-data"><AccordionTrigger data-nonmutating>Show the data</AccordionTrigger><AccordionContent className="grid gap-3">
              <Note>100 is each 3PL's first billed month in the range, so the lines show change, not level.</Note>
              {belowFloorIds.length ? <Note>{belowFloorIds.map((id) => providersById.get(id)?.label ?? id).join(", ")}: under {INDEX_FLOOR} parcels a month, so their lines move with a few orders, not price.</Note> : null}
              {indexGapIds.length ? <Note>{indexGapIds.map((id) => providersById.get(id)?.label ?? id).join(", ")}: no {metricLabel.toLowerCase()} in this range.</Note> : null}
            </AccordionContent></AccordionItem>
          </Accordion>
        </section>
      </>}
    </>}
  </div>;
}
